import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../services/theme.service';
import { User } from '../../../domain/user.model';
import { RoleConstants } from '../../../domain/role.model';

// `items` is narrowed here since the sidebar template recurses on it.
interface SidebarMenuItem extends MenuItem {
  items?: SidebarMenuItem[];
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    standalone: false
})
export class HomeComponent implements OnInit {
  title = 'bild-ui';
  menuItems: SidebarMenuItem[] | undefined;
  profileItems: MenuItem[] | undefined;
  userProfileVisible: boolean = false;
  userDetails: User = {};
  isMobile: boolean = false;
  sidebarVisible: boolean = false;
  currentYear: number = new Date().getFullYear();

  private destroyRef = inject(DestroyRef);

  constructor(private authService: AuthService, private router: Router, public themeService: ThemeService) { }

  ngOnInit() {
    this.loadUserDetails();
    this.validateDevice();

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sidebarVisible = false);

    this.menuItems = [
      {
        label: 'Administración',
        icon: 'pi pi-fw pi-wrench',
        visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS]),
        items: [
          {
            label: 'Usuarios',
            icon: 'pi pi-fw pi-users',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_ENCARGADO]),
            routerLink: '/home/users',
          },
          {
            label: 'Cuentas',
            icon: 'pi pi-fw pi-sitemap',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS]),
            routerLink: '/home/accounts'
          }
        ]
      },
      {
        label: 'Ordenes',
        icon: 'pi pi-fw pi-file-edit',
        visible: false,
        // visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS]),
        routerLink: '/home/orders'
      },
      {
        label: 'Gestión de Inventarios',
        icon: 'pi pi-fw pi-sitemap',
        visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
        items: [
          {
            label: 'Etiquetas',
            icon: 'pi pi-fw pi-tags',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_OPERADOR]),
            items: [
              {
                label: 'Administrar Etiquetas Simples',
                icon: 'pi pi-fw pi-cog',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_OPERADOR]),
                routerLink: '/home/simpleLabels'
              },
              {
                label: 'Administrar Etiquetas de Palet',
                icon: 'pi pi-fw pi-cog',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_OPERADOR]),
                routerLink: '/home/palletLabels'
              }
            ]
          },
          {
            label: 'Artículos',
            icon: 'pi pi-fw pi-th-large',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
            items: [
              {
                label: 'Administrar Artículos',
                icon: 'pi pi-fw pi-cog',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
                routerLink: '/home/inventory/articles',
              },
              {
                label: 'Agregar Artículos',
                icon: 'pi pi-fw pi-plus-circle',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO]),
                routerLink: ['/init-scanner', 'Articles']

              }
            ]
          },
          {
            label: 'Catálogo de Productos',
            icon: 'pi pi-fw pi-book',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
            routerLink: '/home/catalog/products'
          },
          {
            label: 'Palets',
            icon: 'pi pi-fw pi-box',
            visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.BILD_VENTAS, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
            items: [
              {
                label: 'Administrar Palets',
                icon: 'pi pi-fw pi-cog',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO, RoleConstants.CLIENTE_ADMIN, RoleConstants.CLIENTE_OPERADOR]),
                routerLink: '/home/inventory/pallets',
              },
              {
                label: 'Paletizar Artículos',
                icon: 'pi pi-fw pi-plus-circle',
                visible: this.authService.canAccess([RoleConstants.BILD_ADMIN, RoleConstants.BILD_OPERADOR, RoleConstants.BILD_ENCARGADO]),
                routerLink: ['/init-scanner', 'palletization']
              }
            ]
          }
        ]
      },
      {
        label: 'Ayuda',
        icon: 'pi pi-fw pi-question-circle',
        command: () => this.openEmailSender()
      }
    ];

    this.profileItems = [
      {
        label: 'Perfil',
        icon: 'pi pi-fw pi-user',
        items: [
          {
            label: 'Editar perfil',
            icon: 'pi pi-fw pi-cog',
            command: () => {
              this.userProfileVisible = true;
            }
          },
          {
            label: 'Salir',
            icon: 'pi pi-fw pi-power-off',
            command: () => {
              this.userDetails = {};
              this.authService.logout();
            }
          }
        ]
      }
    ];
  }

  validateDevice() {
    const host = window.location.hostname;
    this.isMobile = host.startsWith('m.');
  }

  toggleSidebar() {
    this.sidebarVisible = !this.sidebarVisible;
  }

  // Every time the drawer opens, nested submenus should start collapsed
  // again instead of remembering whatever the user expanded last time.
  resetMenuExpansion() {
    this.collapseMenuItems(this.menuItems);
  }

  private collapseMenuItems(items: SidebarMenuItem[] | undefined) {
    for (const item of items ?? []) {
      item.expanded = false;
      this.collapseMenuItems(item.items);
    }
  }

  onMenuItemClick(item: SidebarMenuItem, event: Event) {
    if (item.command) {
      event.preventDefault();
      item.command({ originalEvent: event, item });
      return;
    }
    if (item.items?.length) {
      event.preventDefault();
      item.expanded = !item.expanded;
    }
  }

  openEmailSender() {
    this.sidebarVisible = false;
    window.location.href = 'mailto:victor.lopez@bild.com.mx?subject=Ayuda%20con%20el%20sistema%20de%20Bild&body=Describe%20tu%20problema%20aquí.';
  }

  private loadUserDetails() {
    const user = this.authService.getUserDetails();
    this.userDetails = user;
  }

  resetUserProfileVisibility() {
    this.userProfileVisible = false;
  }

}
